import { auth } from "@/config/firebase";
import useUser from "@/context/user";
import { Forms } from "@/types/form";
import { log } from "@/utils/logger";
import { updateProfile, User } from "firebase/auth";
import { deleteObject, getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Dispatch, SetStateAction, useState } from "react";
import { UseFormSetValue, UseFormWatch } from "react-hook-form";

const useUpload = (
    setValue: UseFormSetValue<Forms["Profile"]>,
    watch: UseFormWatch<Forms["Profile"]>,
) => {
    const [loading, setLoading] = useState(false);
    const update = useUser(state => state.update);

    const onUpload = async (
        e: React.ChangeEvent<HTMLInputElement>,
        setAnchorEl?: Dispatch<SetStateAction<HTMLElement | null>>,
    ) => {
        const uid = auth.currentUser?.uid;
        const hasImage = watch("photoURL");

        try {
            setLoading(true);
            if (setAnchorEl) {
                setAnchorEl(null);
            }

            const file = e.target.files?.[0];
            if (!file) return;
            const storage = getStorage();
            const storageRef = ref(storage, `avatars/${uid}`);

            if (hasImage) {
                await deleteObject(storageRef);
            }

            const metadata = {
                contentType: "image/jpg",
            };

            await uploadBytes(storageRef, file, metadata);
            const photoURL = await getDownloadURL(storageRef);

            await updateProfile(auth.currentUser as User, {
                photoURL: photoURL,
            });

            setValue("photoURL", photoURL);
            await update();
        } catch (e) {
            log.error(e, "onUpload");
        } finally {
            setLoading(false);
        }
    };

    const onRemove = async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        try {
            setLoading(true);

            const storage = getStorage();
            const storageRef = ref(storage, `avatars/${uid}`);

            await deleteObject(storageRef);

            await updateProfile(auth.currentUser as User, {
                photoURL: "",
            });

            setValue("photoURL", "");
            await update();
        } catch (e) {
            log.error(e, "onDeleteAvatar");
        } finally {
            setLoading(false);
        }
    };

    return { onUpload, onRemove, loading };
};

export { useUpload };
